import React from 'react';
import PropTypes from 'prop-types';
import { Heading, Text } from 'localmed-core';
import styled from '@emotion/styled';
import { margin } from 'styled-system';

import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import isBefore from 'date-fns/isBefore';
import isEqual from 'date-fns/isEqual';
import subDays from 'date-fns/subDays';

import { UniversalContext } from '../../../_universal';
import OpeningsCalendar, { dateShape, openingShape } from '../../../legacyCore/OpeningsCalendar';
import { Scaleup } from '../../../legacyCore/Transitions';
import FirstAvailableOpening from './FirstAvailableOpening';
import CalendarOpening from '../CalendarOpening';
import style from './SchedulingCalendar.scss';
import CalendarDateChangePopup from '../CalendarDateChangePopup';
import WidgetForm from '../../../widgets/components/WidgetForm';
import ForceSelection from '../../../widgets/components/ForceSelection';

const Span = styled('span')`
  white-space: nowrap;
  font-weight: bold;
`;

const OfficeCalendar = styled('div')(
  `
  margin-left: auto;
  margin-right: auto;
  max-width: 1200px;
`,
  margin
);
export default class SchedulingCalendar extends React.Component {
  static propTypes = {
    office: PropTypes.shape({
      display: PropTypes.string,
      phone: PropTypes.string,
      timeZoneCode: PropTypes.string,
    }).isRequired,
    selectedDate: dateShape.isRequired,
    minDate: dateShape.isRequired,
    onChangeSelectedDate: PropTypes.func.isRequired,
    nextOpeningData: PropTypes.shape({
      nextOpening: openingShape,
    }).isRequired,
    openingsData: PropTypes.shape({
      loading: PropTypes.bool,
      openings: PropTypes.shape({
        pageInfo: PropTypes.shape({
          nextAvailableDate: dateShape,
        }).isRequired,
        nodes: PropTypes.arrayOf(openingShape).isRequired,
      }),
    }).isRequired,
    withForm: PropTypes.bool,
    isOnWidget: PropTypes.bool,
    formProps: PropTypes.shape({
      providerDisplay: PropTypes.string,
      patientStatus: PropTypes.string,
      reasonForVisit: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        patientStatus: PropTypes.string.isRequired,
      }),
      reasonForVisitItems: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          patientStatus: PropTypes.string.isRequired,
        })
      ).isRequired,
      selectedPatientStatus: PropTypes.shape({
        id: PropTypes.string.isRequired,
        display: PropTypes.string.isRequired,
      }).isRequired,
      noOnlineSchedulingForStatus: PropTypes.bool,
      onChangePatientStatus: PropTypes.func,
      onChangeReasonForVisit: PropTypes.func,
    }),
  };

  static defaultProps = {
    openingsData: {
      loading: false,
      openings: {
        pageInfo: {},
        nodes: [],
      },
    },
    nextOpeningData: {},
  };

  static contextType = UniversalContext;

  UNSAFE_componentWillUpdate(nextProps) {
    const {
      office,
      reasonForVisit,
      selectedDate,
      openingsData: { openings, loading },
      withForm,
    } = nextProps;
    if (loading || withForm) {
      return;
    }
    this.context.trackEvent({
      event: 'Viewed Office Calendar',
      properties: {
        officeId: office.id,
        officeDisplay: office.display,
        practiceId: office.practice.id,
        practiceDisplay: office.practice.display,
        reasonForVisitId: reasonForVisit.id,
        reasonForVisitDisplay: reasonForVisit.name,
        openingsCount: openings && openings.nodes.length,
        selectedDateDelta: differenceInCalendarDays(selectedDate, new Date()),
        widget: true,
      },
    });
  }

  render() {
    const {
      office,
      selectedDate,
      minDate,
      onChangeSelectedDate,
      nextOpeningData,
      openingsData,
      isOnWidget,
      withForm,
      ...formProps
    } = this.props;

    return (
      <OfficeCalendar {...formProps}>
        <div className={style['office-calendar__header']}>
          <div className={style['office-calendar__header-content']}>
            <h2 className="font-family-meta-bold font-size-md line-height-title margin-none">
              Schedule a Confirmed Appointment
            </h2>
            <CalendarDateChangePopup
              onChangeSelectedDate={onChangeSelectedDate}
              selectedDate={selectedDate}
              minDate={minDate}
            />
          </div>

          <div className={style['office-calendar__first-available']}>
            {nextOpeningData.nextOpening && (
              <Scaleup key={nextOpeningData.nextOpening.id} in>
                <FirstAvailableOpening
                  opening={nextOpeningData.nextOpening}
                  timeZone={office.timeZoneCode}
                  isOnWidget={isOnWidget}
                />
              </Scaleup>
            )}
          </div>
        </div>
        {withForm && <WidgetForm {...formProps} />}
        {withForm && (formProps.noOnlineSchedulingForStatus || !formProps.reasonForVisit) ? (
          <ForceSelection selected={formProps.reasonForVisit != null}>
            {formProps.reasonForVisit && (
              <div className={style['no-penings-for-status']}>
                <Heading tier="h3" fontWeight="bold">
                  {formProps.providerDisplay} is not accepting{' '}
                  {formProps.selectedPatientStatus.id.toLowerCase()} patients online at the moment.
                </Heading>
                <Text>
                  Please call us at <Span>{office.phone}</Span> to schedule your appointment.
                </Text>
              </div>
            )}
          </ForceSelection>
        ) : (
          <OpeningsCalendar
            className={style['office-calendar__calendar']}
            selectedDate={selectedDate}
            minDate={minDate}
            display={formProps.providerDisplay || office.display}
            reasonForVisitName={formProps.reasonForVisit && formProps.reasonForVisit.name}
            phoneNumber={office.phone}
            timeZone={office.timeZoneCode}
            loading={openingsData.loading}
            openings={openingsData.openings && openingsData.openings.nodes}
            nextAvailableDate={
              openingsData.openings && openingsData.openings.pageInfo.nextAvailableDate
            }
            onPreviousClick={() => onChangeSelectedDate(subDays(selectedDate, 7))}
            onNextClick={() => onChangeSelectedDate(addDays(selectedDate, 7))}
            onNextAvailableClick={date => onChangeSelectedDate(date)}
            disablePrevious={isBefore(selectedDate, minDate) || isEqual(selectedDate, minDate)}
            renderOpening={openingProps => (
              <CalendarOpening isOnWidget={isOnWidget} {...openingProps} />
            )}
          />
        )}
      </OfficeCalendar>
    );
  }
}
